import React, { useEffect, useRef, useState } from 'react';

import { climbBridgeAPI } from '../../../lib/api';

export const SPEEDS = [0.5, 1, 2, 4, 8];

/** "1:02:03" or "2:03" back into milliseconds. Blank is zero. */
export function parseDuration(text) {
  const parts = String(text ?? '').trim().split(':').map(p => p.trim());
  if (!parts.length || parts.some(p => p !== '' && !/^\d+$/.test(p))) return null;
  const numbers = parts.map(p => Number(p || 0));
  const seconds = numbers.reduce((total, n) => total * 60 + n, 0);
  return Number.isFinite(seconds) ? seconds * 1000 : null;
}

export function formatDuration(ms) {
  if (ms == null || Number.isNaN(ms)) return '—';
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0');
  return h ? `${h}:${mm}:${String(s).padStart(2, '0')}` : `${mm}:${String(s).padStart(2, '0')}`;
}

/**
 * Play / pause / scrub / speed / loop for a recording that is playing.
 *
 * The position comes from the bridge once a second; between those it is moved
 * on locally at the playback speed, so the scrubber glides instead of jumping.
 * While the operator drags, the server position is ignored until they let go.
 */
/** `control` defaults to the Climb Bridge's; Motorsport Live Timing passes its own. */
export default function PlaybackTransport({ showId, playback, onStop, stopping = false, control: send = climbBridgeAPI.controlPlayback, speeds = SPEEDS }) {
  const [now, setNow] = useState(Date.now());
  const [dragMs, setDragMs] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const receivedAt = useRef(Date.now());

  useEffect(() => { receivedAt.current = Date.now(); }, [playback?.positionMs, playback?.playing, playback?.speed]);

  useEffect(() => {
    if (!playback?.playing) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, [playback?.playing]);

  if (!playback) return null;

  const duration = playback.durationMs || 0;
  const live = playback.playing
    ? Math.min(duration, playback.positionMs + (now - receivedAt.current) * playback.speed)
    : playback.positionMs;
  const position = dragMs ?? live;

  async function control(name, action, params) {
    setBusy(name);
    setError('');
    try {
      await send(showId, action, params);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Playback control failed');
    } finally {
      setBusy('');
    }
  }

  function commitSeek() {
    if (dragMs == null) return;
    const target = dragMs;
    control('seek', 'seek', { position_ms: Math.round(target) }).finally(() => setDragMs(null));
  }

  const jump = (deltaMs) => control('seek', 'seek', { position_ms: Math.round(Math.max(0, Math.min(duration, live + deltaMs))) });

  return (
    <div className="px-4 py-3 bg-[#0f1420] border border-blue-900/60 rounded-lg">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-400">
            {playback.ended ? 'Playback ended' : playback.playing ? 'Playing back' : 'Paused'}
          </div>
          <div className="text-sm text-white truncate" title={playback.name}>{playback.name || 'Recording'}</div>
        </div>
        {onStop && (
          <button
            onClick={onStop}
            disabled={stopping}
            className="flex-shrink-0 px-3 py-1.5 text-xs rounded border border-[#2a2a2a] bg-[#111111] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-40"
          >
            {stopping ? 'Stopping…' : 'Stop playback'}
          </button>
        )}
      </div>

      <input
        type="range"
        min={0}
        max={Math.max(1, duration)}
        step={100}
        value={position}
        onChange={e => setDragMs(Number(e.target.value))}
        onMouseUp={commitSeek}
        onTouchEnd={commitSeek}
        onKeyUp={commitSeek}
        aria-label="Position in recording"
        className="w-full accent-blue-500"
      />

      <div className="flex flex-wrap items-center gap-2 mt-1.5">
        <span className="text-xs text-gray-400 tabular-nums w-28">
          {formatDuration(position)} <span className="text-gray-600">/ {formatDuration(duration)}</span>
        </span>

        <div className="flex items-center gap-1 mx-auto">
          <button onClick={() => jump(-10000)} disabled={!!busy} title="Back 10 seconds" className={TRANSPORT_BTN}>−10s</button>
          {playback.playing ? (
            <button onClick={() => control('pause', 'pause')} disabled={!!busy} className={`${TRANSPORT_BTN} w-20 text-white`}>Pause</button>
          ) : (
            <button onClick={() => control('play', 'play')} disabled={!!busy} className={`${TRANSPORT_BTN} w-20 text-white bg-blue-700 border-blue-600 hover:bg-blue-600`}>
              {playback.ended ? 'Replay' : 'Play'}
            </button>
          )}
          <button onClick={() => jump(10000)} disabled={!!busy} title="Forward 10 seconds" className={TRANSPORT_BTN}>+10s</button>
        </div>

        <div className="flex items-center gap-1">
          {speeds.map(speed => (
            <button
              key={speed}
              onClick={() => control('speed', 'speed', { speed })}
              disabled={!!busy}
              className={`px-2 py-1 text-[11px] rounded border tabular-nums transition-colors ${
                playback.speed === speed ? 'bg-[#1e293b] border-blue-700 text-white' : 'bg-[#111111] border-[#2a2a2a] text-gray-500 hover:text-gray-300'
              }`}
            >
              {speed}×
            </button>
          ))}
          <label className="flex items-center gap-1.5 ml-2 text-[11px] text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={!!playback.loop}
              onChange={e => control('loop', 'loop', { loop: e.target.checked })}
            />
            Loop
          </label>
        </div>
      </div>

      {error && <p className="text-[11px] text-red-400 mt-2">{error}</p>}
    </div>
  );
}

const TRANSPORT_BTN = 'px-2.5 py-1 text-xs rounded border border-[#2a2a2a] bg-[#111111] text-gray-300 hover:border-[#3a3a3a] disabled:opacity-40 transition-colors';
